import type { UnclaimedPlace } from '@brush/api-client';

/**
 * How the room-wide list of unclaimed places (ADR 0013) words what it shows. The page itself only
 * renders these.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A date as `19 Jul 2026`: a named month, never `07/19/2026`, which is two different days depending
 * on who reads it — the same reason the importers refuse anything but `yyyy-mm-dd` on the way in.
 * Built from the string rather than a `Date`, so no time zone can move it a day.
 */
function namedDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1] ?? m} ${y}`;
}

/** One place, as the desk reads it against a player: where, which event, when, and for how much. */
export function placeLine(p: UnclaimedPlace): string {
  return `Place ${p.position} · ${p.tournamentName} (${p.tournamentInternalId}) · ${namedDate(p.date)} · $${p.prize.toLocaleString('en-US')}`;
}

/**
 * The card the results file gave the place, said as what it is. The desk must never register the
 * finisher under it: the source generated it because it could not name them, and the person who comes
 * forward holds a card of their own.
 */
export function fileCardNote(p: UnclaimedPlace): string | null {
  return p.sourceCardId === null
    ? null
    : `The results file gave this place card ${p.sourceCardId}. It is not the player’s card.`;
}

export function unclaimedCount(n: number): string {
  return `${n} unclaimed ${n === 1 ? 'place' : 'places'}`;
}

/** Said, rather than an empty table: an empty list reads as a screen that did not load. */
export function emptyStateSentence(): string {
  return (
    'Every place in this room’s results has a player. A place appears here when a results file ' +
    'records a finish nobody has claimed yet, and leaves when somebody is assigned to it.'
  );
}
